/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import { useAuth0 } from '@auth0/auth0-react'

import { useAppDispatch, useAppSelector } from 'hooks/redux'
import { getLogout } from 'store/slice/loginSlice'
import SidebarSection from 'components/atoms/SidebarSection'
import { getMenuData } from 'components/lab/constants/menu'
import QuickLookup from 'components/lab/menu/QuickLookup'
import { useGetLabOrderCountsQuery } from 'store/services/labOrder'
import { useGetLabNewTicketCountQuery } from 'store/services/tickets'
import { useGetDeviceComplaintsCountQuery } from 'store/services/deviceComplaints'
import { DEVICE_COMPLAINTS_MENU_BADGE } from 'constants/deviceComplaints'
import type { Item } from 'components/atoms/SidebarSection'
import { useLabSavedBuckets } from 'components/lab/state/savedBucketsStore'

const StyledLink = styled(Link)`
  border-bottom: none !important;
`

interface MenuProps {
  isMobileMenuOpen?: boolean
  closeMobileMenu?: () => void
}

const Menu = ({ isMobileMenuOpen = false, closeMobileMenu }: MenuProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const savedBuckets = useLabSavedBuckets()
  const permissions = useAppSelector((state) => state.init.permissions ?? [])
  const { logout } = useAuth0()
  const cachedMenuData = JSON.parse(sessionStorage.getItem('ts-lab-menu') ?? '[]')
  const syncMenuState = (nextMenu: Item[], previousMenu: Item[]): Item[] =>
    nextMenu.map((nextItem) => {
      const previousItem = previousMenu.find(
        (item) => item.label === nextItem.label && item.to === nextItem.to,
      )

      return {
        ...nextItem,
        open: previousItem?.open ?? nextItem.open,
        sub:
          Array.isArray(nextItem.sub) && Array.isArray(previousItem?.sub)
            ? syncMenuState(nextItem.sub, previousItem.sub)
            : nextItem.sub,
      }
    })
  const [menuData, setMenuData] = useState<Item[]>(
    cachedMenuData?.length
      ? syncMenuState(getMenuData(savedBuckets, permissions), cachedMenuData)
      : getMenuData(savedBuckets, permissions),
  )
  const { data: rawOrderCounts, isLoading } = useGetLabOrderCountsQuery(
    { officeId: 1 },
    {
      pollingInterval: 180000,
      refetchOnFocus: true,
    },
  )

  const { data: ticketCounts } = useGetLabNewTicketCountQuery(null, {
    pollingInterval: 120000,
  })

  const { data: complaintCounts } = useGetDeviceComplaintsCountQuery(undefined, {
    pollingInterval: 180000,
  })

  const handleLogout = async () => {
    dispatch(getLogout())
    navigate('/login')
  }
  const orderCounts = [
    ...(rawOrderCounts?.map(({ ordersType, ...i }) => ({ ...i, testType: ordersType })) || []),
    {
      bucket: 'SUPPORT_TICKETS',
      count: ticketCounts?.open ?? 0,
      testType: 'TICKETS',
    },
    {
      ...DEVICE_COMPLAINTS_MENU_BADGE,
      count: complaintCounts?.open ?? 0,
    },
  ]

  const compoundCounts = orderCounts.reduce(
    (agg, curr) => {
      if (agg[curr.bucket] !== undefined) {
        agg[curr.bucket] = agg[curr.bucket] + curr.count
      }
      return agg
    },
    { AUTHORIZATION: 0, BILLING_REVIEW: 0, BILLING_HELD: 0, BILLING_QUEUED: 0, BILLING_CHARGE_BLOCKED: 0, CLIENT_BILL_REVIEW: 0 },
  )

  Object.entries(compoundCounts).forEach(([bucket, count]) =>
    orderCounts.push({
      bucket,
      count,
      testType: 'POX_COX_HST',
    }),
  )

  useEffect(() => {
    sessionStorage.setItem('ts-lab-menu', JSON.stringify(menuData))
  }, [menuData])

  useEffect(() => {
    setMenuData((previousMenu) => syncMenuState(getMenuData(savedBuckets, permissions), previousMenu))
  }, [savedBuckets, permissions])

  return (
    <aside
      className={`main-sidebar lab-sidebar sidebar-dark-primary elevation-4 vh-100 vw-50 ${isMobileMenuOpen ? 'show' : ''}`}>
      <StyledLink to="/" className="brand-link text-center py-4">
        <span className="brand-text">
          <img src="/img/logo.png" width="160px" />
        </span>
      </StyledLink>
      <a
        href="#"
        role="button"
        onClick={handleLogout}
        css={{ position: 'absolute', left: 205, top: 32 }}>
        <i className="fas fa-sign-out-alt text-white" />
      </a>
      <div className="sidebar overflow-y-auto overflow-x-hidden vh-80 pb-10">
        <QuickLookup />
        <nav>
          <ul
            className="nav nav-pills nav-sidebar flex-column"
            data-widget="treeview"
            role="menu"
            data-accordion="false">
            <SidebarSection
              data={menuData}
              setMenuData={setMenuData}
              badgeData={orderCounts}
              closeMobileMenu={closeMobileMenu}
            />
          </ul>
        </nav>
      </div>
    </aside>
  )
}

export default Menu
